import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import finalConfig from '../redux/authentication/utils/core/apiConfig';

// Same target csrfMainManager.js already uses successfully for its own
// health probe (unauthenticated, no DB dependency -- app_health_endpoints.py's
// `/health/live` is a trivial liveness ping, deliberately not the heavier
// `/health` that also checks DB/Redis/Celery, so a slow DB never makes this
// probe itself look slow).
const HEALTH_URL = `${(finalConfig.API.baseUrl || '').replace(/\/$/, '')}/health/live`;

const PING_INTERVAL_HEALTHY = 20000;   // steady-state cadence while online
const PING_INTERVAL_DEGRADED = 5000;   // poll faster while slow/offline, to catch recovery quickly
const PING_TIMEOUT_MS = 6000;
const SLOW_THRESHOLD_MS = 2500;        // ping succeeded but took this long -> "slow", not "offline"
const CONSECUTIVE_FAILURES_FOR_OFFLINE = 2; // don't flip the banner on a single dropped ping

const ConnectivityContext = createContext({
  status: 'online', // 'online' | 'slow' | 'offline'
  latencyMs: null,
  lastOnlineAt: Date.now(),
  checkNow: () => {},
});

export function ConnectivityProvider({ children }) {
  const [status, setStatus] = useState('online');
  const [latencyMs, setLatencyMs] = useState(null);
  const [lastOnlineAt, setLastOnlineAt] = useState(Date.now());

  const failureCountRef = useRef(0);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  const ping = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    const startedAt = performance.now();

    try {
      const response = await fetch(HEALTH_URL, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const elapsedMs = performance.now() - startedAt;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      failureCountRef.current = 0;
      setLatencyMs(Math.round(elapsedMs));
      setLastOnlineAt(Date.now());
      setStatus(elapsedMs > SLOW_THRESHOLD_MS ? 'slow' : 'online');
    } catch {
      failureCountRef.current += 1;
      // A single blip (one dropped ping) doesn't flip the banner -- only a
      // real, sustained failure does, unless the browser itself already says
      // the network interface is down (trust that signal immediately).
      if (!navigator.onLine || failureCountRef.current >= CONSECUTIVE_FAILURES_FOR_OFFLINE) {
        setStatus('offline');
      }
    } finally {
      clearTimeout(timeoutId);
      inFlightRef.current = false;
    }
  }, []);

  const scheduleNext = useCallback((delay) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      await ping();
      // Read the ref, not the `status` state, so a degraded run doesn't need
      // a stale closure over this callback to know to poll faster next time.
      scheduleNext(failureCountRef.current > 0 ? PING_INTERVAL_DEGRADED : PING_INTERVAL_HEALTHY);
    }, delay);
  }, [ping]);

  const checkNow = useCallback(() => {
    scheduleNext(0);
  }, [scheduleNext]);

  useEffect(() => {
    scheduleNext(0);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [scheduleNext]);

  useEffect(() => {
    // Browser-level signals are an immediate, free correction layered on top
    // of the polling loop -- 'offline' is trustworthy on its own (no need to
    // wait for a ping to time out); 'online' only means the network
    // interface is back, not that the API is reachable, so it triggers an
    // immediate re-check rather than being taken at face value.
    const handleOffline = () => {
      failureCountRef.current = CONSECUTIVE_FAILURES_FOR_OFFLINE;
      setStatus('offline');
    };
    const handleOnline = () => checkNow();

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkNow]);

  return (
    <ConnectivityContext.Provider value={{ status, latencyMs, lastOnlineAt, checkNow }}>
      {children}
    </ConnectivityContext.Provider>
  );
}

export const useConnectivity = () => useContext(ConnectivityContext);

export default ConnectivityContext;
